/**
 * Edit 事件 → 显示行 的映射逻辑。
 *
 * 在两处复用：
 * - EditPanel：通过 SSE 实时收到事件，转成 LiveLine 渲染
 * - EditHistory：从 GET /api/sites/:slug/edit/:edit_id/log 拿历史 events 数组，
 *   走完全相同的映射 → 同款显示
 *
 * Server 端的事件 payload 字段（apps/server/src/services/edit-manager.ts EditEvent）：
 *   - type: 'claude_delta' { chunk }
 *   - type: 'tool_use'     { name, input }
 *   - type: 'file_changed' { path, additions, deletions }
 *   - type: 'token_usage'  { input_tokens, output_tokens, cache_creation, cache_read, cost_usd }
 *   - type: 'status'       { phase }
 *   - type: 'error'        { message, stage }
 *   - type: 'completed'    { files_changed, before/after_sha, ... }
 *   - type: 'heartbeat'    （SSE 保活，不渲染）
 *
 * 渲染只关心前 6 类。token_usage 不进行视觉行展示（数字反映在卡片头部"实时用量"区域）。
 */

export type EditLogKind = 'delta' | 'tool' | 'file' | 'status' | 'error';

export interface EditLogLine {
  key: string;
  ts: number;
  kind: EditLogKind;
  text: string;
}

export interface EditTokenSnapshot {
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  cost_usd: number;
}

const ZERO_TOKENS: EditTokenSnapshot = {
  input_tokens: 0,
  output_tokens: 0,
  cache_creation_input_tokens: 0,
  cache_read_input_tokens: 0,
  cost_usd: 0,
};

export function formatEditTime(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

/**
 * 把单个 server EditEvent 映射到 EditLogLine。
 * 返回 null 表示该 event 不展示行（heartbeat / token_usage / completed）。
 *
 * `seq` 由调用方递增，仅用来构造 React 唯一 key（避免同 ts 多事件碰撞）。
 */
export function eventToLine(ev: Record<string, unknown>, seq: number): EditLogLine | null {
  const type = String(ev['type'] ?? '');
  const ts = typeof ev['ts'] === 'number' ? ev['ts'] : Date.now();
  if (type === 'heartbeat' || type === 'token_usage' || type === 'completed') return null;
  if (type === 'claude_delta') {
    const chunk = typeof ev['chunk'] === 'string' ? ev['chunk'] : '';
    if (!chunk) return null;
    return { key: `d${seq}`, ts, kind: 'delta', text: chunk };
  }
  if (type === 'tool_use') {
    const name = typeof ev['name'] === 'string' ? ev['name'] : 'tool';
    const input = ev['input'];
    const file =
      input && typeof input === 'object' && input !== null && 'file_path' in input
        ? String((input as { file_path: unknown }).file_path)
        : '';
    return { key: `t${seq}`, ts, kind: 'tool', text: file ? `${name} ${file}` : name };
  }
  if (type === 'file_changed') {
    const p = typeof ev['path'] === 'string' ? ev['path'] : '';
    const adds = typeof ev['additions'] === 'number' ? ev['additions'] : 0;
    const dels = typeof ev['deletions'] === 'number' ? ev['deletions'] : 0;
    return { key: `f${seq}`, ts, kind: 'file', text: `${p}  +${adds} -${dels}` };
  }
  if (type === 'status') {
    const phase = typeof ev['phase'] === 'string' ? ev['phase'] : '';
    return { key: `s${seq}`, ts, kind: 'status', text: `→ ${phase}` };
  }
  if (type === 'error') {
    const msg = typeof ev['message'] === 'string' ? ev['message'] : 'error';
    const stage = typeof ev['stage'] === 'string' ? ev['stage'] : '';
    return {
      key: `e${seq}`,
      ts,
      kind: 'error',
      text: `✗ ${stage ? stage + ': ' : ''}${msg}`,
    };
  }
  return null;
}

/** 把整个 events 数组映射到行列表，过滤 null。 */
export function eventsToLines(events: ReadonlyArray<Record<string, unknown>>): EditLogLine[] {
  const lines: EditLogLine[] = [];
  events.forEach((ev, i) => {
    const line = eventToLine(ev, i + 1);
    if (line) lines.push(line);
  });
  return lines;
}

/** 累加一组 events 里的 token 用量快照。完成事件里的 total_tokens 优先（最准）。 */
export function aggregateTokenUsage(
  events: ReadonlyArray<Record<string, unknown>>,
): EditTokenSnapshot | null {
  // 优先看 completed.total_tokens
  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i];
    if (!ev || ev['type'] !== 'completed') continue;
    const tt = ev['total_tokens'];
    if (tt && typeof tt === 'object') {
      const t = tt as Partial<EditTokenSnapshot>;
      return {
        input_tokens: Number(t.input_tokens ?? 0),
        output_tokens: Number(t.output_tokens ?? 0),
        cache_creation_input_tokens: Number(t.cache_creation_input_tokens ?? 0),
        cache_read_input_tokens: Number(t.cache_read_input_tokens ?? 0),
        cost_usd: Number(t.cost_usd ?? 0),
      };
    }
  }
  // 否则把所有 token_usage 累加（live SSE 模式）
  const acc: EditTokenSnapshot = { ...ZERO_TOKENS };
  let any = false;
  for (const ev of events) {
    if (ev['type'] !== 'token_usage') continue;
    any = true;
    acc.input_tokens += Number(ev['input_tokens'] ?? 0);
    acc.output_tokens += Number(ev['output_tokens'] ?? 0);
    acc.cache_creation_input_tokens += Number(ev['cache_creation'] ?? 0);
    acc.cache_read_input_tokens += Number(ev['cache_read'] ?? 0);
    acc.cost_usd = Math.max(acc.cost_usd, Number(ev['cost_usd'] ?? 0));
  }
  return any ? acc : null;
}

export const EDIT_LOG_KIND_CLASSNAME: Record<EditLogKind, string> = {
  delta: 'text-slate-700',
  tool: 'text-sky-700',
  file: 'text-indigo-700',
  status: 'text-emerald-700',
  error: 'text-rose-700',
};
